import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from 'react';
import {
  Button,
  ConfirmAlert,
  showAlert,
  Typeahead,
} from '../../../components';
import Table from '../../../components/common/table/Table';
import { FaTrash } from 'react-icons/fa';
import { useProduct } from '../../../context/productContext/ProductContextProvider';
import { useAlternateProduct } from '../../../context/alternateProductContext/AlternateProductContextProvider';
import { useDebouncedCallback } from '@/utils/useDebouncedCallback';

const CreateAlternateProduct = ({
  closeModal,
  data,
  isUpdate,
  setAlternativeValue, // Add this prop
  setShowAlternateProductSection, // Add this prop
}) => {
  const { getAllProduct } = useProduct();
  const {
    alternateProduct,
    setAlternateProduct,
    createNewAlternateProduct,
    updateAlternateProduct,
  } = useAlternateProduct();
  const [productOption, setProductOption] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [alternateQuery, setAlternateQuery] = useState('');
  const [noMainProductFound, setNoMainProductFound] = useState(false);
  const [noAlternateProductFound, setNoAlternateProductFound] = useState(false);
  const typeaheadRef = useRef(null);
  const [productResponse, setProductResponse] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [searchSource, setSearchSource] = useState(null); // 'main' | 'alternate'

  useEffect(() => {
    if (data) {
      setAlternateProduct(data);
    }
  }, [data]);

  const handleQueryChange = useCallback((newQuery) => {
    setSearchQuery(newQuery);
  }, []);

  const handleAlternate = useCallback((newQuery) => {
    setAlternateQuery(newQuery);
  }, []);

  // Debounced effect for Main and Alternate Product search
  useEffect(() => {
    let isActive = true;
    let currentQuery = searchQuery || alternateQuery;

    const searchProducts = async (query, setNoProductFound) => {
      if (query.length >= 2) {
        const response = await getAllProduct(query);

        if (!isActive) return; // Ignore if component unmounted or another query started

        setProductResponse(response);
      } else {
        setProductOption([
          { label: 'Please enter at least 3 characters', id: null },
        ]);
        setNoProductFound(false);
      }
    };

    if (searchQuery) {
      searchProducts(searchQuery, setNoMainProductFound);
    }

    if (alternateQuery) {
      searchProducts(alternateQuery, setNoAlternateProductFound);
    }

    return () => {
      isActive = false; // Mark previous request as stale
    };
  }, [searchQuery, alternateQuery]);

  // Update productOption state and manage "No Data" messages
  useEffect(() => {
    let mainProductTimeout;
    let alternateProductTimeout;

    if (productResponse?.length > 0) {
      setProductOption(
        productResponse?.map((item) => ({
          label: `${item.itemCode} - ${item.itemDescription}`,
          id: item.id,
          data: item,
        })),
      );
      setNoMainProductFound(false);
      setNoAlternateProductFound(false);
    } else {
      // Only show 'No Data' and clear options after timeout
      if (searchQuery?.length >= 3) {
        mainProductTimeout = setTimeout(() => {
          setNoMainProductFound(true);
          setProductOption([]);
        }, 1000);
      }
      if (alternateQuery.length >= 3) {
        alternateProductTimeout = setTimeout(() => {
          setNoAlternateProductFound(true);
          setProductOption([]);
        }, 1000);
      }
    }

    return () => {
      clearTimeout(mainProductTimeout);
      clearTimeout(alternateProductTimeout);
    };
  }, [productResponse, searchQuery, alternateQuery]);

  // Hide alternate product options if no main product is selected
  useEffect(() => {
    if (!alternateProduct?.mainProduct?.id) {
      setProductOption([]);
    }
  }, [alternateProduct?.mainProduct?.id]);

  const removeMainProduct = useCallback(async (event) => {
    event.preventDefault(); // Prevent form submission

    // Show confirmation alert
    const result = await ConfirmAlert({
      confirmBtnText: 'Remove Main Product',
    });

    // If the user did not confirm the removal
    if (!result.isConfirmed) {
      showAlert('error', 'Product removal cancelled.');
      return;
    }

    // Proceed with removing the main product and selected alternate products
    setAlternateProduct((prevState) => ({
      ...prevState,
      mainProduct: {}, // Clear main product ID
      alternateProducts: [], // Clear selected alternate products
    }));

    // Show success message
    showAlert(
      'success',
      'Main product and selected alternate products removed successfully.',
    );
  }, []);

  const removeAlternateProduct = useCallback((productId, event) => {
    event.preventDefault(); // Prevent form submission

    setAlternateProduct((prevState) => ({
      ...prevState,
      alternateProducts: prevState.alternateProducts.filter(
        (productWrapper) => productWrapper.alternateProduct.id !== productId,
      ),
    }));
    showAlert('success', 'Alternate Product Removed');
  }, []);

  const altColumns = useMemo(
    () => [
      { header: 'Item Code', field: 'alternateProduct.itemCode' },
      {
        header: 'Item Description',
        render: (productWrapper) =>
          productWrapper.alternateProduct.itemDescription
            ?.split(' ')
            .slice(0, 6)
            .join(' ') + '...',
      },
      { header: 'Unit', field: 'alternateProduct.unit' },
      { header: 'Price 0 (Ex Gst)', field: 'alternateProduct.price0exGst' },
      { header: 'Avg Cost', field: 'alternateProduct.avgCost' },
      { header: 'Promo Price', field: 'alternateProduct.promoPrice' },
      {
        header: 'Action',
        render: (productWrapper) => (
          <div className="flex justify-center">
            <button
              className="text-red-500"
              onClick={(event) =>
                removeAlternateProduct(
                  productWrapper.alternateProduct.id,
                  event,
                )
              }>
              <FaTrash />
            </button>
          </div>
        ),
      },
    ],
    [removeAlternateProduct],
  );

  const columns = useMemo(
    () => [
      { header: 'Item Code', field: 'itemCode' },
      {
        header: 'Item Description',
        render: (product) =>
          product.itemDescription?.split(' ').slice(0, 6).join(' ') + '...',
      },
      { header: 'Unit', field: 'unit' },
      { header: 'Price 0 (Ex Gst)', field: 'price0exGst' },
      { header: 'Avg Cost', field: 'avgCost' },
      { header: 'Promo Price', field: 'promoPrice' },
      {
        header: 'Action',
        render: (user) => (
          <div className="flex justify-center">
            <button
              className="mr-4 text-red-500"
              onClick={(event) => removeMainProduct(event)}>
              <FaTrash />
            </button>
          </div>
        ),
      },
    ],
    [removeMainProduct],
  );

  const handleMainProductChange = useCallback(
    (selectedOption) => {
      if (selectedOption.length > 0) {
        const selectedProductId = selectedOption[0].id;
        const selectedProduct = productResponse?.find(
          (product) => product.id === selectedProductId,
        );

        // Update the full object for the main product
        setAlternateProduct((prevState) => ({
          ...prevState,
          mainProduct: selectedProduct, // Set selected product object
        }));
        if (typeaheadRef.current) {
          typeaheadRef.current.clear();
        }
        setProductOption([]);
        showAlert('success', 'Main Product Added');
      } else {
        // Reset mainProduct to null if no product is selected
        setAlternateProduct((prevState) => ({
          ...prevState,
          mainProduct: null,
        }));

        // Clear the Typeahead input
        if (typeaheadRef.current) {
          typeaheadRef.current.clear();
        }
      }
    },
    [productResponse],
  );

  const handleAlternateProductChange = useCallback(
    (selectedOption) => {
      if (selectedOption.length > 0) {
        const selectedProductId = selectedOption[0].id;
        const selectedProduct = productResponse?.find(
          (product) => product.id === selectedProductId,
        );

        // Check if the selected product is the same as the main product
        if (
          alternateProduct.mainProduct &&
          selectedProductId === alternateProduct.mainProduct.id
        ) {
          showAlert(
            'error',
            'Cannot add the same product as an alternate product.',
          );
          if (typeaheadRef.current) {
            typeaheadRef.current.clear();
          }
          return;
        }

        // Check if the product is already in the array
        if (
          !alternateProduct?.alternateProducts?.find(
            (prodWrapper) =>
              prodWrapper.alternateProduct.id === selectedProductId,
          )
        ) {
          setAlternateProduct((prevState) => ({
            ...prevState,
            alternateProducts: [
              ...prevState.alternateProducts,
              { alternateProduct: selectedProduct }, // Add the selected product object
            ],
          }));
          showAlert('success', 'Alternate Product Added');
        } else {
          showAlert('error', 'Alternate Product already added');
        }

        // Clear the Typeahead input
        if (typeaheadRef.current) {
          typeaheadRef.current.clear();
        }
      }
    },
    [alternateProduct, productResponse],
  );

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();
      if (!isUpdate) {
        createNewAlternateProduct(alternateProduct, () => {
          closeModal();
          if (setAlternativeValue) setAlternativeValue('Yes'); // Ensure alternate product section is opened
          if (setShowAlternateProductSection)
            setShowAlternateProductSection(true);
        });
      } else {
        updateAlternateProduct(alternateProduct, () => {
          closeModal();
          if (setAlternativeValue) setAlternativeValue('Yes'); // Ensure alternate product section is opened
          if (setShowAlternateProductSection)
            setShowAlternateProductSection(true);
        });
      }
    },
    [
      isUpdate,
      alternateProduct,
      createNewAlternateProduct,
      updateAlternateProduct,
      closeModal,
      setAlternativeValue,
      setShowAlternateProductSection,
    ],
  );

  const debouncedSetSearchQuery = useDebouncedCallback((val) => {
    setSearchQuery(val);
  }, 300);

  const debouncedSetAlternateQuery = useDebouncedCallback((val) => {
    setAlternateQuery(val);
  }, 300);

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setInputValue(val);
    debouncedSetSearchQuery.call(val);
  };
  return (
    <form className="mr-3" onSubmit={handleSubmit}>
      {/* Main Product Section */}
      <div className="flex justify-between items-center">
        <div className="font-600 text-2xl">Select Main Product</div>
        <div className="w-64">
          <Typeahead
            ref={typeaheadRef}
            //onQueryChange={handleQueryChange}
            onQueryChange={(val) => {
              setSearchSource('main');
              debouncedSetSearchQuery.call(val);
            }}
            options={productOption}
            labelKey="label"
            hideErr={true}
            disabledCreateNew={true}
            placeholder="Choose Main product"
            disabled={alternateProduct?.mainProduct?.id ? true : false}
            onChange={handleMainProductChange}
          />
          {!alternateProduct?.mainProduct?.id && noMainProductFound && (
            <div className="text-red-500 m-2 text-sm">
              No Main Product Found
            </div>
          )}
        </div>
      </div>

      {alternateProduct?.mainProduct?.id ? (
        <Table data={[alternateProduct?.mainProduct]} columns={columns} />
      ) : (
        <div className="border-t border-b min-w-[100vh] flex justify-center items-center min-h-80">
          <p className="text-center my-2 font-500 text-xl">Add Main Product</p>
        </div>
      )}

      {/* Alternate Product Section */}
      {alternateProduct?.mainProduct?.id ? (
        <>
          <div className="flex justify-between items-center mt-10">
            <div className="font-600 text-2xl">Select Alternate Product</div>
            <div className="w-64">
              <Typeahead
                ref={typeaheadRef}
                //onQueryChange={handleAlternate}
                onQueryChange={(val) => {
                  setSearchSource('alternate');
                  debouncedSetAlternateQuery.call(val);
                }}
                options={productOption}
                labelKey="label"
                hideErr={true}
                disabledCreateNew={true}
                placeholder="Choose Alternate product"
                onChange={handleAlternateProductChange} // Call handler on product change
              />
              {noAlternateProductFound && (
                <div className="text-red-500 mt-2">
                  No Alternate Product Found
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-100 rounded-2xl">
            {alternateProduct?.alternateProducts?.length > 0 ? (
              <Table
                data={alternateProduct.alternateProducts}
                columns={altColumns}
              />
            ) : (
              <div className="border-t border-b min-w-[100vh] flex justify-center items-center">
                <p className="text-center my-2 font-500 text-xl">
                  No Alternate Products Added
                </p>
              </div>
            )}
          </div>
        </>
      ) : (
        <></>
      )}

      <div className="flex justify-end mx-10 mt-5">
        <Button>
          <div>{isUpdate ? 'Update' : 'Submit'}</div>
        </Button>
      </div>
    </form>
  );
};

export default CreateAlternateProduct;
